import { useQueries } from '@tanstack/react-query';
import { previewOrderPrice } from '@/api';
import type { Order, ServiceCatalogueItem } from '@/types';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export interface ServicePriceQuote {
  fee: number | null;
  isLoading: boolean;
}

/**
 * SVC-STEP — what THIS order costs on each service, quoted side by side.
 *
 * The create-order wizard has always priced exactly one service: the resolved
 * one. Showing a comparison means asking the same question once per service,
 * which is what this does — `POST /merchants/orders/preview` with the row's
 * `service_code` substituted, one query per service, in parallel.
 *
 * ponytail: N services = N requests. That is 2 today and the catalogue is
 * hand-curated by ops, so a bulk quote endpoint would be machinery for a
 * problem nobody has. Add `POST /orders/preview/services` if the catalogue
 * ever outgrows a handful — the call site here is one `useQueries`.
 *
 * A FAILED quote is not a blocked service. The row renders without a price and
 * stays selectable, because eligibility is the backend's answer to give: it
 * refuses with SERVICE_NO_ELIGIBLE_CARRIER at creation and never silently
 * downgrades. Treating a preview error as "unavailable" here would put a
 * second, drifting source of truth about eligibility in the client — and the
 * merchant would be blocked with no reason shown, instead of being told one.
 */
const useServicePriceQuotes = (
  orderPayload: Order | null,
  services: ServiceCatalogueItem[],
): Record<string, ServicePriceQuote> => {
  const results = useQueries({
    queries: services.map((service) => ({
      // The payload is part of the key: change the parcel and every quote is a
      // different question. Stringifying it is crude but it is the whole input
      // to the request, so nothing can drift out of the key by being forgotten.
      queryKey: ['service-price-quote', service.code, JSON.stringify(orderPayload ?? {})],
      queryFn: () => previewOrderPrice({ ...(orderPayload as Order), service_code: service.code }),
      enabled: Boolean(orderPayload),
      staleTime: FIVE_MINUTES_MS,
      retry: false,
    })),
  });

  return services.reduce<Record<string, ServicePriceQuote>>((quotes, service, index) => {
    const result = results[index];
    const fee = result?.data?.object?.orders_shipment_fee;

    quotes[service.code] = {
      fee: typeof fee === 'number' ? fee : null,
      isLoading: Boolean(result?.isLoading) && Boolean(orderPayload),
    };

    return quotes;
  }, {});
};

export default useServicePriceQuotes;
